import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NavController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { merge, Subscription } from 'rxjs';
import { take, takeWhile } from 'rxjs/operators';
import { AppState, AuthService, EventService, SessionAttributionService } from '../services';
import { SearchActions } from '../actions';
import ErrorHandler from '../../lib/error-handler';

// The step-1 answer, stashed through login (savedSearch store on the way out,
// router state on the way back). Opaque to Angular beyond truthiness.
type SearchDraft = {
  mode: 'state' | 'federal';
  firstName: string;
  lastName: string;
  state?: string;
  inmateId?: string;
  birthdate?: string;
};

type PmReactSearchNavigateIntent =
  | { to: 'login'; query?: SearchDraft }
  | { to: 'my-penmates' }
  | { to: 'penmate-profile'; penmateId: string }
  | { to: 'compose'; penmateId: string };

// Funnel events from the bundle. The event's `type` is the metrics event name;
// everything else is the payload. `search-add-succeeded` is client-only (it
// triggers the GTM conversion; the Amplitude add event is server-emitted).
type PmReactSearchEvent = {
  type:
    | 'search-opened'
    | 'search-started'
    | 'search-match-presented'
    | 'search-match-confirmed'
    | 'search-match-declined'
    | 'search-wait-resolved'
    | 'search-flow-left'
    | 'search-add-succeeded';
  [key: string]: any;
};

// Inbound pre-fill from the URL (marketing/guide links, facility cards).
// Uncommitted: it seeds the first screen and never fires a search.
type SearchPrefill = {
  firstName?: string;
  lastName?: string;
  state?: string;
  facilityId?: string;
  facilitySlug?: string;
};

type PmReactSearchProps = {
  isAuthenticated?: boolean;
  user?: any;
  sessionId?: number;
  resumeQuery?: SearchDraft;
  prefill?: SearchPrefill;
  getMetricsContext?: () => { [key: string]: any } | undefined;
  onNavigate?: (intent: PmReactSearchNavigateIntent) => void;
  onEvent?: (event: PmReactSearchEvent) => void;
};

type PmReactSearchApi = {
  mount: (el: Element, props: PmReactSearchProps) => void;
  update: (el: Element, props: PmReactSearchProps) => void;
  unmount: (el: Element) => void;
  leave?: (el: Element) => void;
};

const JS_URL = '/assets/www/react/search/pmreact-search.js';

const getSearchPrefetchUrl = (): string | null => {
  if (typeof document === 'undefined') return null;
  const link = document.querySelector(
    'link[as="script"][href*="pmreact-search.js"][rel="preload"], link[as="script"][href*="pmreact-search.js"][rel="prefetch"]',
  ) as HTMLLinkElement | null;
  return link && typeof link.href === 'string' && link.href ? link.href : null;
};

const getSearchBundleUrl = (): string => {
  const w = window as any;
  const env = w && w.Penmate && w.Penmate.env ? w.Penmate.env : {};
  const envName = String(env.name || env.environment || '');
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const isDev =
    /development/i.test(envName) || /(^localhost$|^127\.0\.0\.1$|ngrok\.io$)/i.test(host);
  if (!isDev) return getSearchPrefetchUrl() || JS_URL;
  // In development we want a fresh bundle after rebuilds; stable filenames + SPA navigation
  // otherwise make it easy to end up with a stale cached JS artifact.
  const sep = JS_URL.includes('?') ? '&' : '?';
  return `${JS_URL}${sep}v=${Date.now()}`;
};

const getSearchFallbackUrl = (primaryUrl: string): string | null => {
  if (!primaryUrl) return null;
  try {
    const primary = new URL(primaryUrl, document.baseURI);
    const fallback = new URL(JS_URL, document.baseURI);
    if (primary.origin === fallback.origin && primary.pathname === fallback.pathname) {
      return null;
    }
  } catch {
    if (primaryUrl === JS_URL) return null;
  }
  return JS_URL;
};

type ScriptLoadVerify = () => boolean;

type ScriptLoadErrorRecord = {
  message?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  stack?: string;
  reason?: string;
};

type ScriptDiagnostics = {
  url: string;
  script?: {
    src?: string;
    loaded?: boolean;
    failed?: boolean;
    async?: boolean;
    crossOrigin?: string | null;
  } | null;
  scripts?: string[];
  performance?: {
    transferSize?: number;
    encodedBodySize?: number;
    decodedBodySize?: number;
    duration?: number;
  } | null;
  fetch?: {
    status?: number;
    contentType?: string | null;
    contentLength?: string | null;
    contentEncoding?: string | null;
    etag?: string | null;
    cacheControl?: string | null;
    cfCacheStatus?: string | null;
    age?: string | null;
    via?: string | null;
    xCache?: string | null;
    server?: string | null;
    snippetStart?: string | null;
    snippetEnd?: string | null;
    error?: string;
  } | null;
  errors?: ScriptLoadErrorRecord[];
  pmReactPresent?: boolean;
};

const getScriptState = (id: string): ScriptDiagnostics['script'] => {
  if (typeof document === 'undefined') return null;
  const script = document.getElementById(id) as HTMLScriptElement | null;
  if (!script) return null;
  return {
    src: script.src,
    loaded: (script as any).__pmLoaded,
    failed: (script as any).__pmFailed,
    async: script.async,
    crossOrigin: script.crossOrigin,
  };
};

const getPerformanceEntry = (url: string): ScriptDiagnostics['performance'] | null => {
  if (typeof performance === 'undefined' || !url) return null;
  const entries = performance.getEntriesByName(url);
  if (!entries || !entries.length) return null;
  const entry = entries[entries.length - 1] as PerformanceResourceTiming;
  return {
    transferSize: entry.transferSize,
    encodedBodySize: entry.encodedBodySize,
    decodedBodySize: entry.decodedBodySize,
    duration: entry.duration,
  };
};

const collectBundleDiagnostics = async (url: string, id: string): Promise<ScriptDiagnostics> => {
  const diagnostics: ScriptDiagnostics = {
    url,
    script: getScriptState(id),
    scripts:
      typeof document === 'undefined'
        ? []
        : Array.from(document.querySelectorAll('script[src*="pmreact-search"]')).map((el) =>
            (el as HTMLScriptElement).src,
          ),
    performance: getPerformanceEntry(url),
    fetch: null,
    errors: [],
    pmReactPresent:
      typeof window !== 'undefined' && !!((window as any).PmReact && (window as any).PmReact.search),
  };
  if (!url || typeof fetch === 'undefined') return diagnostics;
  try {
    const response = await fetch(url, {
      cache: 'no-store',
      mode: 'cors',
      headers: {
        Range: 'bytes=0-1023',
      },
    });
    const text = await response.text();
    diagnostics.fetch = {
      status: response.status,
      contentType: response.headers.get('content-type'),
      contentLength: response.headers.get('content-length'),
      contentEncoding: response.headers.get('content-encoding'),
      etag: response.headers.get('etag'),
      cacheControl: response.headers.get('cache-control'),
      cfCacheStatus: response.headers.get('cf-cache-status'),
      age: response.headers.get('age'),
      via: response.headers.get('via'),
      xCache: response.headers.get('x-cache'),
      server: response.headers.get('server'),
      snippetStart: text.slice(0, 200),
      snippetEnd: text.length > 200 ? text.slice(-200) : null,
    };
  } catch (err) {
    diagnostics.fetch = {
      error: err instanceof Error ? err.message : String(err),
    };
  }
  return diagnostics;
};

const captureScriptErrors = (bundleUrl: string): { errors: ScriptLoadErrorRecord[]; stop: () => void } => {
  const errors: ScriptLoadErrorRecord[] = [];
  if (typeof window === 'undefined') return { errors, stop: () => undefined };
  const matchesBundle = (value?: string | null): boolean =>
    typeof value === 'string' && value.length ? value.includes('pmreact-search') || value.includes(bundleUrl) : false;

  const onError = (event: ErrorEvent) => {
    if (!event) return;
    const filename = event.filename || (event.error && (event.error as any).fileName);
    const stack = event.error instanceof Error ? event.error.stack : undefined;
    if (!matchesBundle(filename) && !matchesBundle(stack)) return;
    errors.push({
      message: event.message,
      filename,
      lineno: event.lineno,
      colno: event.colno,
      stack,
    });
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    const reason = (event && (event as any).reason) || null;
    const message = reason instanceof Error ? reason.message : reason ? String(reason) : undefined;
    const stack = reason instanceof Error ? reason.stack : undefined;
    if (!matchesBundle(message) && !matchesBundle(stack)) return;
    errors.push({
      message,
      reason: message,
      stack,
    });
  };

  window.addEventListener('error', onError, true);
  window.addEventListener('unhandledrejection', onRejection);

  return {
    errors,
    stop: () => {
      window.removeEventListener('error', onError, true);
      window.removeEventListener('unhandledrejection', onRejection);
    },
  };
};

const markScriptFailed = (id: string): void => {
  if (typeof document === 'undefined') return;
  const script = document.getElementById(id) as HTMLScriptElement | null;
  if (!script) return;
  (script as any).__pmFailed = true;
  (script as any).__pmLoaded = false;
  script.remove();
};

const getSearchScriptSnapshot = (id: string): Record<string, unknown> => {
  if (typeof window === 'undefined') return { unavailable: true };
  const w = window as any;
  const pmReact = w.PmReact;
  const pmReactKeys =
    pmReact && typeof pmReact === 'object' ? Object.keys(pmReact).slice(0, 20) : null;
  let scriptSnapshot: Record<string, unknown> | null = null;
  if (typeof document !== 'undefined') {
    const script = document.getElementById(id) as HTMLScriptElement | null;
    if (script) {
      scriptSnapshot = {
        src: script.src || null,
        async: script.async,
        defer: script.defer,
        type: script.type || null,
        crossOrigin: script.crossOrigin || null,
        readyState: (script as any).readyState || null,
        pmLoaded: (script as any).__pmLoaded || false,
        pmFailed: (script as any).__pmFailed || false,
      };
    }
  }
  return {
    hasPmReact: !!pmReact,
    pmReactType: typeof pmReact,
    hasSearch: !!(pmReact && pmReact.search),
    pmReactKeys,
    script: scriptSnapshot,
  };
};

const addCacheBust = (url: string): string => {
  try {
    const parsed = new URL(url, document.baseURI);
    parsed.searchParams.set('pmcb', String(Date.now()));
    return parsed.toString();
  } catch {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}pmcb=${Date.now()}`;
  }
};

const loadScriptOnce = (
  src: string,
  id: string,
  opts?: { verifyGlobal?: ScriptLoadVerify },
): Promise<void> => {
  const verifyGlobal = opts && opts.verifyGlobal;
  const hasGlobal = () => (typeof verifyGlobal === 'function' ? verifyGlobal() : true);
  const existingById = document.getElementById(id) as HTMLScriptElement | null;
  if (existingById) {
    if ((existingById as any).__pmFailed) {
      existingById.remove();
    } else {
      const desiredSrc = new URL(src, document.baseURI).toString();
      const existingSrc = existingById.src ? new URL(existingById.src, document.baseURI).toString() : '';

      if (existingSrc && existingSrc !== desiredSrc) {
        existingById.remove();
      } else if ((existingById as any).__pmLoaded && !hasGlobal()) {
        (existingById as any).__pmFailed = true;
        existingById.remove();
      } else {
        return new Promise((resolve, reject) => {
          if ((existingById as any).__pmLoaded) return resolve();
          if (hasGlobal()) return resolve();
          if ((existingById as any).__pmFailed) {
            return reject(new Error(`Failed to load ${src}`));
          }
          existingById.addEventListener(
            'load',
            () => {
              if (!hasGlobal()) {
                (existingById as any).__pmFailed = true;
                return reject(new Error('PmReact.search is not available after bundle load'));
              }
              resolve();
            },
            { once: true },
          );
          existingById.addEventListener(
            'error',
            () => reject(new Error(`Failed to load ${src}`)),
            { once: true },
          );
        });
      }
    }
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.addEventListener('load', () => {
      (script as any).__pmLoaded = true;
      if (!hasGlobal()) {
        (script as any).__pmFailed = true;
        reject(new Error('PmReact.search is not available after bundle load'));
        return;
      }
      resolve();
    });
    script.addEventListener('error', () => {
      (script as any).__pmFailed = true;
      reject(new Error(`Failed to load ${src}`));
    });
    document.body.appendChild(script);
  });
};

@Component({
  selector: 'pm-page-react-search',
  templateUrl: 'react-search.html',
  styleUrls: ['./react-search.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ReactSearchPage implements AfterViewInit, OnDestroy {
  @ViewChild('host', { static: true }) hostRef: ElementRef;

  private isMounted = false;
  private isAlive = true;
  private bootStarted = false;
  private routeSyncSub: Subscription | null = null;
  private isAuthenticated = false;
  private user: any = null;
  private resumeQuery: SearchDraft | null = null;
  private prefill: SearchPrefill | null = null;
  private authResolved = false;

  isBooting = true;
  bootError: string | null = null;

  constructor(
    private zone: NgZone,
    private route: ActivatedRoute,
    private navCtrl: NavController,
    private authService: AuthService,
    private eventService: EventService,
    private sessionAttribution: SessionAttributionService,
    private store: Store<AppState>,
    private searchActions: SearchActions,
  ) {}

  ngAfterViewInit(): void {
    if (this.bootStarted) return;
    this.bootStarted = true;

    this.isBooting = true;
    this.bootError = null;

    this.readRoutedQuery();

    // The add/search surface renders for logged-out visitors too (the flow starts
    // here and auth happens at the commitment point), so authentication must not
    // gate the mount — it only enriches the props once it resolves.
    this.authService
      .authenticate()
      .pipe(take(1), takeWhile(() => this.isAlive))
      .subscribe(
        ({ isAuthenticated, user }) => {
          this.authResolved = true;
          this.isAuthenticated = !!(isAuthenticated && user);
          this.user = user || null;
          if (!this.isMounted) return;
          try {
            this.render();
          } catch {
            // ignore render races
          }
        },
        () => {
          // Auth probe failure is non-fatal for this surface: treat as logged out
          // so a commit can proceed to login rather than wait forever.
          this.authResolved = true;
          this.isAuthenticated = false;
          if (this.isMounted) {
            try {
              this.render();
            } catch {
              // ignore render races
            }
          }
        },
      );

    void this.bootReactSearch();
  }

  private async attemptBundleLoad(
    url: string,
    label: 'primary' | 'fallback',
    fallbackUrl: string | null,
  ): Promise<{ ok: boolean; error?: Error }> {
    const scriptId = 'pmreact-search-js';
    const errorCapture = captureScriptErrors(url);
    let loadError: Error | null = null;
    try {
      await loadScriptOnce(url, scriptId, {
        verifyGlobal: () => !!this.getApi(),
      });
    } catch (err) {
      loadError = err instanceof Error ? err : new Error(String(err));
    } finally {
      errorCapture.stop();
    }

    if (!loadError) return { ok: true };

    markScriptFailed(scriptId);
    const diagnostics = await collectBundleDiagnostics(url, scriptId);
    diagnostics.errors = errorCapture.errors;
    ErrorHandler.captureException(loadError, `React search bundle load failure (${label})`, {
      bundleUrl: url,
      fallbackUrl,
      prefetchUrl: getSearchPrefetchUrl(),
      diagnostics,
    });
    return { ok: false, error: loadError };
  }

  private async loadSearchBundle(bundleUrl: string, fallbackUrl: string | null): Promise<void> {
    const primary = await this.attemptBundleLoad(bundleUrl, 'primary', fallbackUrl);
    if (primary.ok) return;
    if (!fallbackUrl) throw primary.error;

    const fallbackAttemptUrl = addCacheBust(fallbackUrl);
    const fallback = await this.attemptBundleLoad(fallbackAttemptUrl, 'fallback', fallbackAttemptUrl);
    if (fallback.ok) {
      ErrorHandler.captureMessage('React search loaded via fallback', {
        bundleUrl,
        fallbackUrl: fallbackAttemptUrl,
        prefetchUrl: getSearchPrefetchUrl(),
      });
      return;
    }
    throw fallback.error || primary.error;
  }

  private async bootReactSearch(): Promise<void> {
    const bundleUrl = getSearchBundleUrl();
    const fallbackUrl = getSearchFallbackUrl(bundleUrl);
    try {
      await this.loadSearchBundle(bundleUrl, fallbackUrl);
      // The page can be destroyed while the bundle is still loading; mounting
      // then would leak a React tree on a detached host element.
      if (!this.isAlive) return;
      this.render();
      this.startRouteSync();
      requestAnimationFrame(() => {
        if (!this.isAlive) return;
        this.zone.run(() => {
          this.isBooting = false;
        });
      });
    } catch (err) {
      if (!this.isAlive) return;
      // eslint-disable-next-line no-console
      console.error(err);
      const error = err instanceof Error ? err : new Error(String(err));
      ErrorHandler.captureException(error, 'React search boot error', {
        bundleUrl,
        fallbackUrl,
        prefetchUrl: getSearchPrefetchUrl(),
        scriptSnapshot: getSearchScriptSnapshot('pmreact-search-js'),
      });
      this.zone.run(() => {
        this.isBooting = false;
        this.bootError = 'Please try again.';
      });
    }
  }

  // A draft carried through login arrives as router state on the post-login
  // navigation (auth.service). Read it once, then consume it — history.state
  // survives reloads of the entry, and a re-read would re-fire the search.
  // The inbound contract the marketing pages send: first_name, last_name,
  // state (the legacy page read facility_state — accept both), facility_id
  // (+ facility_slug) from facility cards. Attribution params are captured
  // separately at boot.
  private readPrefill(): void {
    const params = this.route && this.route.snapshot ? this.route.snapshot.queryParamMap : null;
    if (!params) return;
    const pick = (key: string): string | undefined => {
      const value = params.get(key);
      return value && value.trim() ? value.trim() : undefined;
    };
    const prefill: SearchPrefill = {
      firstName: pick('first_name'),
      lastName: pick('last_name'),
      state: pick('state') || pick('facility_state'),
      facilityId: pick('facility_id'),
      facilitySlug: pick('facility_slug'),
    };
    const any = Object.keys(prefill).some((key) => !!(prefill as any)[key]);
    this.prefill = any ? prefill : null;
  }

  private readRoutedQuery(): void {
    this.readPrefill();
    const routedQuery =
      typeof history !== 'undefined' && history.state ? history.state.searchQuery : null;
    const isDraft =
      routedQuery &&
      typeof routedQuery === 'object' &&
      routedQuery.firstName &&
      routedQuery.lastName &&
      (routedQuery.mode === 'federal' || routedQuery.state);
    if (!isDraft) return;
    this.resumeQuery = routedQuery as SearchDraft;
    try {
      const nextState = Object.assign({}, history.state, { searchQuery: null });
      history.replaceState(nextState, '');
    } catch {
      // Consuming the state is best-effort; the worst case is a re-fire on reload.
    }
  }

  // Ionic reuses a cached view for the same URL, skipping ngAfterViewInit on
  // re-entry — re-probe auth and router state so a login completed without a
  // full reload can't strand the page on stale props.
  ionViewWillEnter(): void {
    if (!this.bootStarted) return;
    this.readRoutedQuery();
    this.authService
      .authenticate()
      .pipe(take(1), takeWhile(() => this.isAlive))
      .subscribe(
        ({ isAuthenticated, user }) => {
          this.authResolved = true;
          this.isAuthenticated = !!(isAuthenticated && user);
          this.user = user || null;
          if (!this.isMounted) return;
          try {
            this.render();
          } catch {
            // ignore render races
          }
        },
        () => {
          // Auth probe failure is non-fatal for this surface.
        },
      );
  }

  // Leaving the page (another tab, the messenger, login) is the flow's
  // abandonment signal; the bundle decides whether there is anything to report.
  // Did-leave, not will-leave: will-leave fires at the START of an edge
  // swipe-back, so an aborted swipe would emit a false leave; did-leave fires
  // only when the transition actually completed, and still precedes destroy.
  ionViewDidLeave(): void {
    if (!this.isMounted) return;
    const el = (this.hostRef && this.hostRef.nativeElement) as Element | undefined;
    const api = this.getApi();
    if (el && api && typeof api.leave === 'function') {
      try {
        api.leave(el);
      } catch {
        // analytics must never break navigation
      }
    }
  }

  ngOnDestroy(): void {
    this.isAlive = false;
    const el = (this.hostRef && this.hostRef.nativeElement) as Element | undefined;
    const api = this.getApi();
    if (el && api && typeof api.unmount === 'function') api.unmount(el);
    this.isMounted = false;
    if (this.routeSyncSub) {
      this.routeSyncSub.unsubscribe();
      this.routeSyncSub = null;
    }
  }

  private getApi(): PmReactSearchApi | null {
    const w = window as any;
    return (w && w.PmReact && w.PmReact.search) || null;
  }

  // The inner metrics_context value (session attribution) for the add POST;
  // undefined when the session has no attribution to carry.
  private metricsContextValue(): { [key: string]: any } | undefined {
    try {
      const wrapped = this.sessionAttribution.metricsContext() as any;
      return wrapped && wrapped.metrics_context ? wrapped.metrics_context : undefined;
    } catch {
      return undefined;
    }
  }

  private render(): void {
    const el = (this.hostRef && this.hostRef.nativeElement) as Element | undefined;
    if (!el) return;

    const api = this.getApi();
    if (!api) throw new Error('PmReact.search is not available after bundle load');

    const props: PmReactSearchProps = {
      // Undefined until the first auth probe settles — the bundle holds a
      // commit made in that window rather than treating it as logged-out.
      isAuthenticated: this.authResolved ? this.isAuthenticated : undefined,
      user: this.user || undefined,
      sessionId:
        this.eventService && (this.eventService as any) && (this.eventService as any).sessionId
          ? (this.eventService as any).sessionId
          : undefined,
      resumeQuery: this.resumeQuery || undefined,
      prefill: this.prefill || undefined,
      getMetricsContext: () => this.metricsContextValue(),
      onEvent: (event) => {
        this.zone.run(() => {
          if (!event || !event.type) return;
          if (event.type === 'search-add-succeeded') {
            // GTM ad-conversion parity with the legacy add path; the metrics
            // add-penmate event is emitted server-side with the search linkage.
            try {
              this.eventService.trackAddedPenmateConversion({
                penmate_id: event.penmate_id || undefined,
              });
            } catch {
              // analytics must never break the flow
            }
            return;
          }
          // Null values are dropped: the server's payload sanitizer turns nil
          // into {} (nil responds to to_h), which would poison the property.
          const payload: { [key: string]: any } = {};
          Object.keys(event).forEach((key) => {
            if (key !== 'type' && event[key] !== null && event[key] !== undefined) {
              payload[key] = event[key];
            }
          });
          Promise.resolve(this.eventService.track(event.type, payload)).catch(() => undefined);
        });
      },
      onNavigate: (intent) => {
        this.zone.run(() => {
          if (!intent || !intent.to) return;
          if (intent.to === 'login') {
            // Save-through-login: stash the committed draft so auth.service can
            // return it to this surface as router state after authentication. The
            // login page also reads snake_case names from router state for its
            // "Connect with {name}" copy.
            if (intent.query) {
              this.store.dispatch(this.searchActions.saveSearchQuery(intent.query));
              this.navCtrl.navigateForward('/login', {
                state: {
                  searchQuery: {
                    first_name: intent.query.firstName,
                    last_name: intent.query.lastName,
                  },
                },
              });
              return;
            }
            this.navCtrl.navigateForward('/login');
            return;
          }
          if (intent.to === 'my-penmates') {
            this.navCtrl.navigateRoot('/my-penmates');
            return;
          }
          if (intent.to === 'penmate-profile' && intent.penmateId) {
            this.navCtrl.navigateForward(`/penmates/${encodeURIComponent(intent.penmateId)}`);
            return;
          }
          if (intent.to === 'compose' && intent.penmateId) {
            // The handoff's first-letter CTA — straight into the messenger.
            this.navCtrl.navigateForward(
              `/penmates/${encodeURIComponent(intent.penmateId)}/messenger`,
            );
            return;
          }
        });
      },
    };

    this.zone.runOutsideAngular(() => {
      if (!this.isMounted) {
        api.mount(el, props);
        this.isMounted = true;
      } else {
        api.update(el, props);
      }
    });
  }

  private startRouteSync(): void {
    if (this.routeSyncSub) return;
    this.routeSyncSub = merge(this.route.paramMap, this.route.queryParamMap)
      .pipe(takeWhile(() => this.isAlive))
      .subscribe(() => {
        if (!this.isMounted) return;
        // Keep React props in sync with URL changes, including a new inbound
        // pre-fill arriving on this retained page instance.
        this.readPrefill();
        try {
          this.render();
        } catch (e) {
          // If navigation races script load/unmount, ignore and let the next navigation/render win.
        }
      });
  }
}
